import React from 'react'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Badge } from 'primereact/badge'
import DateBodyTemplate from 'components/common/CommonGrids/Columns/DateBodyTemplate'
import { getAquaBadgeClass, getOrangeBadgeClass, getGreenBadgeClass } from 'components/common/CommonGrids/utils/badgeUtils'

/**
 * Date formatter using DateBodyTemplate
 */
export const dateFormatter = (rowData, field) => DateBodyTemplate({ rowData, field })

/**
 * Higher-order function: Creates a template with copy button
 * @param {string} componentId - Component ID for element IDs
 * @param {string} field - Field name to display
 * @param {object} options - Configuration options
 * @param {function} options.formatter - Custom formatter function
 * @param {boolean} options.showNavButton - Show navigation button
 * @param {boolean} options.navDisabled - Disable navigation button
 * @param {function} options.onNavigate - Navigation handler
 * @param {string} options.className - Additional CSS classes
 * @param {boolean} options.tooltip - Show tooltip
 * @param {object} options.style - Inline styles
 * @param {boolean} options.allowEmpty - Allow empty values
 */
export const createCopyButtonTemplate = (componentId, field, options = {}) => (rowData) => {
    const value = rowData[field]
    if (!value && !options.allowEmpty) return ''
    
    const displayValue = options.formatter ? options.formatter(rowData, field) : value
    
    return (
        <div
            id={componentId + '_contactList_commonGrid_' + field}
            className="d-flex justify-content-start align-items-center"
            style={options.style}
        >
            <span 
                className={options.className || ''}
                title={options.tooltip ? displayValue : undefined}
            >
                {displayValue}
            </span>
            {displayValue && (
                <div className="d-flex ml-auto">
                    <Button 
                        icon="pi pi-copy" 
                        className="white ml-2" 
                        rounded 
                        onClick={() => navigator.clipboard.writeText(displayValue)} 
                    />
                    {options.showNavButton && (
                        <Button 
                            className="white ml-2" 
                            icon="fa-light fa-arrow-up-right" 
                            rounded 
                            disabled={options.navDisabled}
                            onClick={options.onNavigate}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

/**
 * Contact type badge formatter with dynamic aqua shading
 */
export const createContactTypeBadgeTemplate = () => (rowData) => {
    const typeName = rowData.contactTypeName
    const typeId = rowData.contactTypeId
    if (!typeName) return null
    
    return (
        <Badge
            value={typeName}
            className={`custom-badge-base ${getAquaBadgeClass(typeId)}`}
        />
    )
}

/**
 * Company type badge formatter with dynamic green shading
 * Looks up company type name from assets based on companyType (SapCode)
 * @param {Array} companyTypes - Array of company types from assets
 */
export const createCompanyTypeBadgeTemplate = (companyTypes = []) => (rowData) => {
    const companyTypeId = rowData.companyType
    if (!companyTypeId) return null
    
    // Parse to number to match the id type from the API
    const companyTypeIdNum = parseInt(companyTypeId, 10)
    
    // Find the company type by id
    const companyTypeObj = companyTypes.find(ct => ct.id === companyTypeIdNum)
    const typeName = companyTypeObj?.description || companyTypeId
    const typeId = companyTypeObj?.id || 0
    
    return (
        <Badge
            value={typeName}
            className={`custom-badge-base ${getGreenBadgeClass(typeId)}`}
        />
    )
}

/**
 * Membership types formatter with dynamic orange badges
 */
export const createMembershipTypesTemplate = () => (rowData) => {
    const memberships = rowData.memberships
    if (!memberships || !Array.isArray(memberships) || memberships.length === 0) {
        return null
    }
    
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center' }}>
            {memberships.map((membership, index) => (
                <Badge 
                    key={index} 
                    value={membership.membershipTypeName} 
                    className={`custom-badge-base ${getOrangeBadgeClass(membership.membershipTypeId)}`} 
                />
            ))}
        </div>
    )
}

/**
 * On Hold checkbox template
 */
export const createOnHoldTemplate = (componentId) => (rowData) => {
    return (
        <div 
            id={componentId + '_contactList_commonGrid_isOnHold'} 
            className="d-flex justify-content-center align-items-center"
        >
            <Checkbox
                checked={rowData.isOnHold}
                disabled
            />
        </div>
    )
}

/**
 * Works avatar template with expand/collapse functionality
 * @param {object} expandedRows - Currently expanded rows object
 * @param {function} setExpandedRows - Function to update expanded rows
 */
export const createWorksAvatarTemplate = (expandedRows, setExpandedRows) => (rowData) => {
    if (!rowData.hasWorks) return null
    
    const handleWorksClick = (e) => {
        e.preventDefault()
        e.stopPropagation()
        
        const currentExpanded = expandedRows || {}
        const isExpanded = currentExpanded[rowData.contactId]
        
        let newExpandedRows
        if (isExpanded) {
            const { [rowData.contactId]: removed, ...rest } = currentExpanded
            newExpandedRows = Object.keys(rest).length > 0 ? rest : null
        } else {
            newExpandedRows = {
                ...currentExpanded,
                [rowData.contactId]: rowData
            }
        }
        
        setExpandedRows(newExpandedRows)
    }
    
    const isExpanded = expandedRows && expandedRows[rowData.contactId]
    
    return (
        <Button 
            type="button"
            className={`p-button-rounded p-button-icon-only ml-2 ${isExpanded ? 'p-button-outlined' : ''}`}
            rounded 
            onClick={handleWorksClick}
            aria-label={isExpanded ? "Collapse works" : "Expand works"}
        >
            W
        </Button>
    )
}
